/**
 * Lead pipeline enums — status + priority. Mirrored across
 * mobile + web. Lead source / project type / possession status
 * are NOT here — those are form-only enums that don't drive UI
 * gates and are duplicated less aggressively.
 */

export type LeadStatus =
  | 'new'
  | 'contacted'
  | 'site_visit_scheduled'
  | 'proposal_sent'
  | 'negotiation'
  | 'converted'
  | 'lost';

/** Pipeline stages for filtering / stepper (includes terminals). */
export const LEAD_STATUSES: { key: LeadStatus; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'site_visit_scheduled', label: 'Site visit' },
  { key: 'proposal_sent', label: 'Proposal' },
  { key: 'negotiation', label: 'Negotiation' },
  { key: 'converted', label: 'Converted' },
  { key: 'lost', label: 'Lost' },
];

/** Active pipeline before win/loss (for progress indication). */
export const LEAD_PIPELINE_ACTIVE: LeadStatus[] = [
  'new',
  'contacted',
  'site_visit_scheduled',
  'proposal_sent',
  'negotiation',
];

export function getLeadStatusLabel(status: LeadStatus): string {
  return LEAD_STATUSES.find((s) => s.key === status)?.label ?? status;
}

/** Priority — `hot` added per the redesigned lead form. Existing
 *  data uses low/medium/high; old reads remain valid. */
export type LeadPriority = 'low' | 'medium' | 'high' | 'hot';

export const LEAD_PRIORITIES: { key: LeadPriority; label: string }[] = [
  { key: 'low', label: 'Low' },
  { key: 'medium', label: 'Medium' },
  { key: 'high', label: 'High' },
  { key: 'hot', label: 'Hot' },
];

export function getLeadPriorityLabel(p: LeadPriority): string {
  return LEAD_PRIORITIES.find((x) => x.key === p)?.label ?? p;
}

// ── Lead intake form enums ───────────────────────────────────
// These don't drive UI security gates, but the lead-create
// form on mobile + web both need identical option lists so the
// dropdowns stay in sync and stored doc values are portable.

/** Where the lead came from — the source-of-truth attribution. */
export type LeadSource =
  | 'walk_in'
  | 'reference'
  | 'instagram'
  | 'website'
  | 'just_dial'
  | 'google_ads'
  | 'other';

export const LEAD_SOURCES: { key: LeadSource; label: string }[] = [
  { key: 'walk_in', label: 'Walk-in' },
  { key: 'reference', label: 'Reference' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'website', label: 'Website' },
  { key: 'just_dial', label: 'JustDial' },
  { key: 'google_ads', label: 'Google Ads' },
  { key: 'other', label: 'Other' },
];

export function getLeadSourceLabel(source: LeadSource): string {
  return LEAD_SOURCES.find((s) => s.key === source)?.label ?? source;
}

/** Project type the lead is asking about. `renovation` added per
 *  the redesigned form; `office` kept for legacy reads. */
export type ProjectType =
  | '1bhk'
  | '2bhk'
  | '3bhk'
  | 'villa'
  | 'office'
  | 'commercial'
  | 'renovation'
  | 'other';

export const PROJECT_TYPES: { key: ProjectType; label: string }[] = [
  { key: '1bhk',       label: '1BHK Interior' },
  { key: '2bhk',       label: '2BHK Interior' },
  { key: '3bhk',       label: '3BHK Interior' },
  { key: 'villa',      label: '4BHK+ / Villa' },
  { key: 'commercial', label: 'Commercial / Office' },
  { key: 'renovation', label: 'Renovation' },
  { key: 'office',     label: 'Office' },         // legacy
  { key: 'other',      label: 'Other' },
];

export function getProjectTypeLabel(t: ProjectType): string {
  return PROJECT_TYPES.find((x) => x.key === t)?.label ?? t;
}

/** State of the client's property when the lead is captured. */
export type PossessionStatus =
  | 'under_construction'
  | 'ready_to_move'
  | 'old_property';

export const POSSESSION_STATUSES: { key: PossessionStatus; label: string }[] = [
  { key: 'under_construction', label: 'Under Construction' },
  { key: 'ready_to_move',      label: 'Ready to Move' },
  { key: 'old_property',       label: 'Old Property (Renovation)' },
];

export function getPossessionStatusLabel(p: PossessionStatus): string {
  return POSSESSION_STATUSES.find((x) => x.key === p)?.label ?? p;
}

/** Budget bucketed into ranges. Replaces the free-number `budget`
 *  for new writes. Existing leads still have `budget`; both can
 *  coexist. */
export type BudgetRange =
  | 'under_10l'
  | '10_15l'
  | '15_25l'
  | '25_40l'
  | 'above_40l';

export const BUDGET_RANGES: { key: BudgetRange; label: string }[] = [
  { key: 'under_10l',  label: 'Under ₹10L' },
  { key: '10_15l',     label: '₹10L – ₹15L' },
  { key: '15_25l',     label: '₹15L – ₹25L' },
  { key: '25_40l',     label: '₹25L – ₹40L' },
  { key: 'above_40l',  label: 'Above ₹40L' },
];

export function getBudgetRangeLabel(r: BudgetRange): string {
  return BUDGET_RANGES.find((x) => x.key === r)?.label ?? r;
}
